import Experience from "@folio-2026/experience"
import assert from "assert"
import {
  color,
  hash,
  instancedBufferAttribute,
  instanceIndex,
  max,
  mix,
  shapeCircle,
  texture,
  time,
  uniform,
  vec2,
  vec3,
} from "three/tsl"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

export default class OuterParticles {
  experience: Experience
  fbm: THREE.Texture
  perlin: THREE.Texture
  scene: THREE.Object3D
  debugFolder?: FolderApi
  sprite!: THREE.Sprite
  material!: THREE.PointsNodeMaterial
  positionAttribute!: THREE.InstancedBufferAttribute
  lifeAttribute!: THREE.InstancedBufferAttribute

  man!: THREE.SkinnedMesh
  manGeometry!: THREE.BufferGeometry
  manPositionAttribute!:
    | THREE.BufferAttribute
    | THREE.InterleavedBufferAttribute
  randomIndices: number[] = []
  tempVector = new THREE.Vector3()

  settings = {
    count: 50,
    size: uniform(0.09),
    minSize: uniform(0.04), // Minimum size for random size distribution
    color1: uniform(color("#3f3f3f")),
    color2: uniform(color("#1d2836")),
    yThreshold: 0.65, // Percentage of Y range to exclude from bottom
    smoothstepRange: 0.2, // Range for smoothstep transition (e.g., 0.2 = 20% of Y range)
    maxOffset: uniform(2), // Maximum Y offset before particle resets
    speed: uniform(0.08), // Animation speed (units per second)
    divergenceFreq: uniform(0.4),
    divergenceAmp: uniform(1.5),
  }

  constructor({
    scene,
    debugFolder,
    man,
    fbm,
    perlin,
  }: {
    scene: THREE.Object3D
    debugFolder?: FolderApi
    man: THREE.SkinnedMesh
    fbm: THREE.Texture
    perlin: THREE.Texture
  }) {
    this.experience = Experience.getInstance()
    this.scene = scene
    this.debugFolder = debugFolder
    this.man = man
    this.fbm = fbm
    this.perlin = perlin

    this.init()
    this.createParticles()
    this.setDebug()
  }

  init() {
    this.manGeometry = this.man.geometry

    // Get position attribute from geometry
    const positionAttribute = this.manGeometry.getAttribute("position")
    assert(positionAttribute, "Position attribute not found")

    // Cache positionAttribute
    this.manPositionAttribute = positionAttribute

    /**
     *
     * Calculate randomIndices, positionAttribute and lifeAttribute at runtime
     * These values are cached for performance reasons
     *
     * Do not remove the following code block
     *
     */
    // Calculate Y range and threshold
    const maxIndex = positionAttribute.count
    let minY = Infinity
    let maxY = -Infinity

    for (let i = 0; i < maxIndex; i++) {
      const y = positionAttribute.getY(i)
      if (y < minY) minY = y
      if (y > maxY) maxY = y
    }

    const yRange = maxY - minY
    const yThreshold = minY + yRange * this.settings.yThreshold
    const smoothstepStart = yThreshold
    const smoothstepEnd = yThreshold + yRange * this.settings.smoothstepRange

    // Smoothstep function: t * t * (3 - 2 * t)
    const smoothstep = (t: number) => {
      t = Math.max(0, Math.min(1, t)) // Clamp to [0, 1]
      return t * t * (3 - 2 * t)
    }

    // Store random indices for consistent vertex selection
    this.randomIndices = []
    const v = []

    // Select vertices using probability-based selection
    while (this.randomIndices.length < this.settings.count) {
      const randomIndex = Math.floor(Math.random() * maxIndex)
      const vertexY = positionAttribute.getY(randomIndex)

      // Skip vertices below threshold
      if (vertexY < yThreshold) {
        continue
      }

      // Calculate selection probability using smoothstep
      let probability = 1.0
      if (vertexY < smoothstepEnd) {
        // In transition zone: use smoothstep
        const normalizedY =
          (vertexY - smoothstepStart) / (smoothstepEnd - smoothstepStart)
        probability = smoothstep(normalizedY)
      }

      // Use probability to decide if vertex is selected
      if (Math.random() < probability) {
        this.randomIndices.push(randomIndex)

        v.push(
          positionAttribute.getX(randomIndex),
          positionAttribute.getY(randomIndex),
          positionAttribute.getZ(randomIndex),
        )
      }
    }
    // console.log("save randomIndices", this.randomIndices)
    // console.log("save v", v)

    this.positionAttribute = new THREE.InstancedBufferAttribute(
      new Float32Array(v),
      3,
    )

    // Create life attribute with random values between 0.3 and maxOffset
    const lifeValues: number[] = []
    const minLife = 0.3
    const maxLife = this.settings.maxOffset.value

    for (let i = 0; i < this.settings.count; i++) {
      const life = minLife + Math.random() * (maxLife - minLife)
      lifeValues.push(life)
    }

    // console.log("save lifeValues", lifeValues)
    this.lifeAttribute = new THREE.InstancedBufferAttribute(
      new Float32Array(lifeValues),
      1,
    )
  }

  updatePositions() {
    const pos = this.tempVector
    for (let i = 0; i < this.settings.count; i++) {
      const randomIndex = this.randomIndices[i]
      pos.fromBufferAttribute(this.manPositionAttribute, randomIndex)
      // Apply bone transform to get position in man's local space
      this.man.applyBoneTransform(randomIndex, pos)

      // Transform from man's local space to world space
      pos.applyMatrix4(this.man.matrixWorld)

      // Store base position (offset will be applied in shader)
      this.positionAttribute.setXYZ(i, pos.x, pos.y, pos.z)
    }

    this.positionAttribute.needsUpdate = true
  }

  createParticles() {
    this.material = new THREE.PointsNodeMaterial({
      sizeAttenuation: false,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    })

    // Calculate offset in shader using time and instanceIndex
    const basePos = instancedBufferAttribute(this.positionAttribute)
    const life = instancedBufferAttribute(this.lifeAttribute)

    // Generate random starting phase for each particle using hash
    const randomPhase = hash(instanceIndex).mul(life)

    const speed = this.settings.speed.mul(randomPhase.add(0.2))

    // Divergence scale
    const divScale = life.remap(0, 1, 0, 0.5)

    // Oscillazione
    const fbmSample = texture(
      this.perlin,
      vec2(basePos.x.mul(this.settings.divergenceFreq), 1),
    )
      .mul(2)
      .sub(1)
    const noise = vec2(max(fbmSample.x, 0))
      .mul(this.settings.divergenceAmp)
      .mul(divScale)

    const yOffset = time
      .mul(speed)
      .add(randomPhase)
      .mod(life)
      .mul(life)
      .mul(3)
      .pow(0.6)
      .div(2)

    this.material.positionNode = vec3(
      basePos.x.add(noise.x),
      basePos.y.add(yOffset),
      basePos.z.add(noise.y),
    )

    const sizeRange = this.settings.size.sub(this.settings.minSize)
    const particleSize = this.settings.minSize.add(randomPhase.mul(sizeRange))
    this.material.sizeNode = life.sub(yOffset).mul(particleSize).mul(100)

    this.material.colorNode = mix(
      this.settings.color1,
      this.settings.color2,
      randomPhase,
    )

    this.material.opacityNode = shapeCircle().mul(
      life.sub(yOffset).mul(0.5).add(0.5),
    )

    this.sprite = new THREE.Sprite(this.material)
    this.sprite.count = this.settings.count
    this.sprite.frustumCulled = false
    // this.sprite.renderOrder = RENDER_ORDER.MAN + 1

    this.scene.add(this.sprite)
  }

  update() {
    this.updatePositions()
  }

  setDebug() {
    if (!this.debugFolder) return

    const folder = this.debugFolder.addFolder({
      title: "Outer Particles",
      expanded: false,
    })

    folder.addBinding(this.settings.minSize, "value", {
      label: "Min Size",
      min: 0.001,
      max: 0.1,
      step: 0.001,
    })

    folder.addBinding(this.settings.size, "value", {
      label: "Size",
      min: 0.001,
      max: 0.1,
      step: 0.001,
    })

    // Color control
    const colorSettings = {
      color1: `#${new THREE.Color(this.settings.color1.value).getHexString()}`,
      color2: `#${new THREE.Color(this.settings.color2.value).getHexString()}`,
    }

    folder
      .addBinding(colorSettings, "color1", {
        view: "color",
      })
      .on("change", (ev) => {
        this.settings.color1.value = new THREE.Color(ev.value)
      })

    folder
      .addBinding(colorSettings, "color2", {
        view: "color",
      })
      .on("change", (ev) => {
        this.settings.color2.value = new THREE.Color(ev.value)
      })

    // Max offset control
    folder.addBinding(this.settings.maxOffset, "value", {
      label: "Max Offset",
      min: 0.1,
      max: 10,
      step: 0.1,
    })

    // Speed control
    folder.addBinding(this.settings.speed, "value", {
      label: "Speed",
      min: 0,
      max: 1,
      step: 0.01,
    })

    // Divergence frequency control
    folder
      .addBinding(this.settings.divergenceFreq, "value", {
        label: "Divergence Frequency",
        min: 0.01,
        max: 3,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.divergenceFreq.value = ev.value
      })

    // Divergence amplitude control
    folder
      .addBinding(this.settings.divergenceAmp, "value", {
        label: "Divergence Amplitude",
        min: 0,
        max: 10,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.divergenceAmp.value = ev.value
      })
  }
}
